package com.kinoshazam.data

import com.kinoshazam.ACCESS_TOKEN
import com.kinoshazam.REFRESH_TOKEN
import com.kinoshazam.data.models.AuthResult
import com.kinoshazam.data.models.LoginResult
import io.github.aakira.napier.Napier
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.auth.authProvider
import io.ktor.client.plugins.auth.providers.BearerAuthProvider
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

class TokenRepository(
    private val storageUser: Storage,
    private val storageSecure: Storage,
    private val httpClient: HttpClient,
    private val authHttpClient: Lazy<HttpClient>,
    private val globalStateRepository: GlobalStateRepository,
) {

    private val refreshMutex = Mutex()

    fun getAccessToken(): String? = storageSecure.get<String>(ACCESS_TOKEN)
    fun getRefreshToken(): String? = storageSecure.get<String>(REFRESH_TOKEN)

    suspend fun saveTokens(access: String, refresh: String) {
        storageSecure.put(REFRESH_TOKEN, refresh)
        storageSecure.put(ACCESS_TOKEN, access)
        clearBearerCache()
        globalStateRepository.setAuth(true)
    }

    suspend fun clear() {
        storageSecure.clear()
        clearBearerCache()
        globalStateRepository.setAuth(false)
        globalStateRepository.setProfile(null)
    }

    /**
     * Serialized refresh with rotation-safe retry.
     * Definitive auth failures clear the local session; transient network errors do not.
     */
    suspend fun refresh(): BearerTokens? = refreshMutex.withLock {
        val refreshToken = getRefreshToken() ?: return null

        return try {
            when (val result = refresh(refreshToken).result) {
                is LoginResult.LoggedIn -> {
                    saveTokens(result.accessToken, result.refreshToken)
                    BearerTokens(result.accessToken, result.refreshToken)
                }
                else -> tokensIfRotated(refreshToken) ?: invalidateSession("refresh rejected")
            }
        } catch (e: Exception) {
            Napier.w("Token refresh failed: ${e.message}")
            tokensIfRotated(refreshToken) ?: if (isUnauthorized(e)) {
                invalidateSession("refresh unauthorized")
            } else {
                // Transient network / server error — keep local session.
                null
            }
        }
    }

    private suspend fun invalidateSession(reason: String): BearerTokens? {
        Napier.w("Clearing session: $reason")
        clear()
        return null
    }

    private fun tokensIfRotated(attemptedRefresh: String): BearerTokens? {
        val currentRefresh = getRefreshToken() ?: return null
        if (currentRefresh == attemptedRefresh) return null
        val access = getAccessToken() ?: return null
        return BearerTokens(access, currentRefresh)
    }

    private fun isUnauthorized(e: Exception): Boolean {
        if (e is ClientRequestException) {
            val code = e.response.status
            return code == HttpStatusCode.Unauthorized || code == HttpStatusCode.Forbidden
        }
        return false
    }

    private fun clearBearerCache() {
        runCatching {
            authHttpClient.value.authProvider<BearerAuthProvider>()?.clearToken()
        }
    }

    private suspend fun refresh(refreshToken: String): AuthResult<LoginResult.LoggedIn> {
        return httpClient.post("auth/refresh") {
            setBody(
                AuthTokenRefresh(refreshToken)
            )
        }.body()
    }
}
